import { useEffect, useMemo, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import CLUB from '../config/club.js'
import { buildTvRotation, buildTeamNames, shortCode, photoPermalink, groupByTime } from '../lib/photoCuration.js'
import { lastNames } from '../lib/teamName.js'
import AwardReveal from './AwardReveal.jsx'

// Passive auto-advancing slideshow of APPROVED photos — the TV wall
// (/tv/photos, /tv/split, admin monitor tile) and the phone "▶ Play" overlay.
//
// The wall plays HOUR BY HOUR: each hourly batch's photos roll in an anti-burst
// order, then that hour ENDS with an awards-ceremony finale crowning its two AI
// winners — the top PHOTO and (if any) the top VIDEO — before moving to the next
// hour. Winners come from each block's aiInterest (topPhoto/topVideo); an hour
// with no video simply skips the video award (never forced).
const SLIDE_MS = 5000
const ANNOUNCE_MS = 3900       // build-up beat (logo + "…and it goes to…")
const PHOTO_HOLD_MS = 15_000   // a winning PHOTO holds ~15s on screen
const VIDEO_SAFETY_MS = 60_000 // fallback if a video never fires onEnded (it normally advances after 2 plays)

// Hourly "photo/video of the hour" award finale — OFF for now. Flip to true to
// bring back the hourly hold (the AwardReveal path below is kept intact so it's
// a one-line re-enable).
const SHOW_HOURLY_AWARDS = false

// Flatten the wall into an ordered PROGRAM of steps.
//  • Awards OFF (default now): just play EVERY photo in the order given
//    (newest-first, as PhotoWallApp passes them) — no grouping, no dedup, no
//    hourly hold, so nothing is dropped.
//  • Awards ON: each hour's slides followed by its finale award(s), oldest hour
//    first, so the newest hour is the climax before the loop repeats.
function buildProgram(photos) {
  if (!SHOW_HOURLY_AWARDS) {
    return (photos || []).map((p) => ({ type: 'slide', photo: p }))
  }
  const steps = []
  const blocks = [...groupByTime(photos)].reverse() // chronological (oldest → newest)
  for (const b of blocks) {
    const rot = buildTvRotation(b.photos, {
      teamCap: 2, now: Date.now(), burstWindowMs: 5 * 60_000, burstMax: 2, dedupWindowMs: 10_000,
    })
    for (const p of rot) steps.push({ type: 'slide', photo: p })
    if (b.topPhoto) steps.push({ type: 'award', kind: 'photo', photo: b.topPhoto, label: b.label })
    if (b.topVideo) steps.push({ type: 'award', kind: 'video', photo: b.topVideo, label: b.label })
  }
  return steps
}

export default function PhotoSlideshow({ photos, tournament, tournamentId = null, videoUnmuted, embedded }) {
  // `now` recomputes as `photos` changes (live approvals) so ordering stays fresh.
  const program = useMemo(() => buildProgram(photos), [photos])
  const teamNames = useMemo(() => buildTeamNames(tournament?.flights), [tournament])

  const [pos, setPos] = useState(0)
  const [awardPhase, setAwardPhase] = useState('announce')
  const [videoProgress, setVideoProgress] = useState(0)
  const [portrait, setPortrait] = useState(null)
  const len = program.length
  const hovered = useRef(false)
  const videoRef = useRef(null)

  useEffect(() => { if (pos >= len && len > 0) setPos(0) }, [len, pos])

  const step = len ? program[pos % len] : null
  const isAward = step?.type === 'award'
  const slide = step?.type === 'slide' ? step.photo : null
  const isVideoSlide = slide?.type === 'video'

  const advance = () => setPos((n) => (len ? (n + 1) % len : 0))

  // A fresh award step always starts on the announce beat.
  useEffect(() => { if (isAward) setAwardPhase('announce') }, [pos, isAward])

  // Photo slide → timed auto-advance (hover pauses). Video slide advances onEnded.
  useEffect(() => {
    if (!slide || isVideoSlide) return
    const id = setInterval(() => { if (!hovered.current) advance() }, SLIDE_MS)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, slide, isVideoSlide])

  // Award finale → announce build-up, then reveal. A PHOTO holds ~15s; a VIDEO
  // holds until it has played through twice (AwardReveal → onVideoDone), with a
  // safety cap so a stuck video can never freeze the wall.
  useEffect(() => {
    if (!isAward) return
    if (awardPhase === 'announce') {
      const t = setTimeout(() => setAwardPhase('reveal'), ANNOUNCE_MS)
      return () => clearTimeout(t)
    }
    const ms = step.kind === 'video' ? VIDEO_SAFETY_MS : PHOTO_HOLD_MS
    const t = setTimeout(advance, ms)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, isAward, awardPhase])

  useEffect(() => { setVideoProgress(0); setPortrait(null) }, [pos])
  useEffect(() => { if (videoRef.current) videoRef.current.muted = !videoUnmuted }, [videoUnmuted, pos])

  if (!step) return null

  const awardName = (p) => {
    const tn = p?.teamId ? teamNames[p.teamId] : null
    return tn ? lastNames(tn) : (p?.submittedBy || 'Guest')
  }

  // ── Awards finale takes over the whole wall ──
  if (isAward) {
    return (
      <AwardReveal
        key={`${pos}-${step.photo.id}`}
        award={{ kind: step.kind, photo: step.photo, label: step.label, phase: awardPhase }}
        who={awardName(step.photo)}
        embedded={embedded}
        videoUnmuted={videoUnmuted}
        onVideoDone={advance}
      />
    )
  }

  // ── Normal hour rotation slide ──
  const teamName = slide.teamId ? (teamNames[slide.teamId] || null) : null
  const code = shortCode(slide.id)

  return (
    <div
      className={`${embedded ? 'h-full' : 'min-h-screen'} relative bg-black overflow-hidden`}
      onMouseEnter={() => { hovered.current = true }}
      onMouseLeave={() => { hovered.current = false }}>
      {/* Blurred fill backdrop — the same photo, cover-scaled + blurred, so a
          PORTRAIT's side gaps read as a clean soft fill instead of dead black
          bars. A full-bleed LANDSCAPE (object-cover) sits on top and fully
          occludes it, so this only shows where the foreground can't reach. */}
      {!isVideoSlide ? (
        <img
          key={`bg-${slide.id}`}
          src={slide.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'blur(30px) brightness(0.5)', transform: 'scale(1.12)' }}
        />
      ) : null}
      <div className="absolute inset-0 flex items-center justify-center">
        {isVideoSlide ? (
          <video
            ref={videoRef}
            key={slide.id}
            src={slide.url}
            poster={slide.posterUrl || undefined}
            className="w-full h-full object-contain animate-fade"
            autoPlay
            muted={!videoUnmuted}
            playsInline
            onEnded={advance}
            onTimeUpdate={(e) => {
              const el = e.currentTarget
              const dur = Number(el.duration) || 0
              if (dur > 0) setVideoProgress(Math.min(1, el.currentTime / dur))
            }}
          />
        ) : (
          <img
            key={slide.id}
            src={slide.url}
            alt={slide.caption || ''}
            onLoad={(e) => setPortrait(e.currentTarget.naturalHeight > e.currentTarget.naturalWidth)}
            className={`animate-fade ${portrait === null ? 'max-w-full max-h-full object-contain' : portrait ? 'h-full w-auto max-w-none' : 'w-full h-full object-cover'}`}
          />
        )}
      </div>

      {isVideoSlide ? (
        <div className="absolute left-0 right-0" style={{ top: 0, height: '4px', backgroundColor: 'rgba(255,255,255,0.10)' }}>
          <div style={{ width: `${Math.round(videoProgress * 100)}%`, height: '100%', backgroundColor: CLUB.accentColor, transition: 'width 200ms linear' }} />
        </div>
      ) : null}

      {/* Bottom overlay — labeled with time · team · find-this code. */}
      <div
        className="absolute left-0 right-0 bottom-0 px-8 py-5 text-white"
        style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0) 100%)' }}>
        <div className="flex justify-between items-end gap-6 flex-wrap">
          <div className="min-w-0">
            {/* Short caption: team LAST NAMES · hole · time/date. Nothing else. */}
            <div className="text-lg text-white/90 flex flex-wrap gap-3 items-baseline">
              <span className="font-extrabold text-white text-xl">{teamName ? lastNames(teamName) : (slide.submittedBy || 'Guest')}</span>
              {slide.hole != null ? <span>· Hole {slide.hole}</span> : null}
              <span>· {formatTime(slide.timestamp)}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {tournamentId && !embedded ? (
              <div className="rounded-md bg-white p-1.5 shrink-0" title="Scan to open this photo">
                <QRCodeCanvas value={`${window.location.origin}${photoPermalink(tournamentId, slide.id)}`} size={64} level="M" />
              </div>
            ) : null}
            <div className="text-right text-sm" style={{ color: CLUB.accentColor }}>
              <div className="text-sm uppercase tracking-widest opacity-80">Find this · #{code}</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade { from { opacity: 0.0 } to { opacity: 1 } }
        .animate-fade { animation: fade 700ms ease-out; }
      `}</style>
    </div>
  )
}

function formatTime(ts) {
  if (!ts) return ''
  return new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
